import PropTypes from 'prop-types';
import { useState } from 'react';
import http from 'lc-http';
import { Grid } from '@ui-propulsion/react-components';
import styles from './emailCapture.module.scss';
import Spinner from '../spinner/spinner';

export default function EmailCapture({
  placeHolderText = 'Enter your email',
  buttonText = 'Sign Up',
  textSuccess = 'Success! You have been added to the notification list.',
  textFailed = 'Unable to add you to the notification list. Please try again.',
  successCallback = () => {},
  errorCallback = () => {},
  type = 'newsletter',
  state = '',
  subContext = 'email-signup',
}) {
  const [email, setEmail] = useState('');
  const [validationProps, setValidationProps] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);
  const [successMsg, setSuccessMessage] = useState(null); // This holds the success message after submission
  const newsLetterEndPoint = '/api/news-letter-sign-up';
  const emailSenderEndPoint = '/api/email-sender';
  const mailbody = {
    email: `${email}`,
    emailState: `${state}`,
    from: 'funnel@lendingclub.com',
    to: 'support@lendingclub.com',
    subject: 'Auto Launch - Non Qualifying States',
    page: 'LANDING-AUTO-IMPL-PAGE',
  };

  const validate = () => {
    const textEmptyEmail = 'Please enter your email';
    const textInvalidEmail = 'Please enter valid email';
    if (!email) {
      setValidationProps({
        error: { label: textEmptyEmail },
        isValid: false,
      });
      return false;
    }

    if (!email.match(/^[A-Za-z0-9._%+-]+@(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,4}$/)) {
      setValidationProps({
        error: { label: textInvalidEmail },
        isValid: false,
      });
      return false;
    }

    setValidationProps({ isValid: true });
    return true;
  };

  // To set cookies for successfull subscription
  /* const setEmailCaptureCookies = () => {
    const unlimitedExpiry = new Date(2147483647 * 1000).toUTCString();
    cookies.set('newsletter-popup-submitted', true, {
      expires: unlimitedExpiry,
    });
  }; */

  const handleErrorSendingEmail = (error) => {
    setValidationProps({
      error: { label: textFailed },
      isValid: false,
    });
    setShowSpinner(false);
    setEmail('');
    errorCallback(error);
  };

  const handleSuccessSendingEmail = (response) => {
    if (response.status && response.status === 200) {
      // setEmailCaptureCookies();
      setSuccessMessage(textSuccess);
      setShowSpinner(false);
      setEmail('');
      successCallback();
    }
  };

  const sendEmail = () => {
    setShowSpinner(true);
    if (type === 'email sender') {
      http
        .post(emailSenderEndPoint, mailbody)
        .then(handleSuccessSendingEmail)
        .catch(handleErrorSendingEmail);
    } else {
      http
        .post(
          newsLetterEndPoint,
          `email=${encodeURIComponent(
            email
          )}&context=lc-website&subContext=${subContext}`
        )
        .then(handleSuccessSendingEmail)
        .catch(handleErrorSendingEmail);
    }
  };

  const handleEmailSubmit = (evt) => {
    evt.preventDefault();
    if (!validate(email)) return;
    sendEmail();
  };

  // Clear success message when user starts typing again
  const handleInputChange = (e) => {
    setEmail(e.target.value);
    setSuccessMessage(null); // Clear success message when user types
    setValidationProps(null); // Reset validation props
  };

  const classValidSpan = successMsg
    ? `${styles.mailInputSuccess} ${styles.successCheck}`
    : styles.mailInputSuccess;

  const classSubmitButton = buttonText
    ? styles.button
    : `${styles.button} ${styles.iconSend}`;

  return (
    <div className={styles.emailCapture}>
      <form
        className={styles.emailForm}
        onSubmit={(e) => handleEmailSubmit(e)}
        method="POST"
      >
        <div className={styles.formElements} data-container="form-elements">
          <Grid container spacing={1}>
            <Grid item xs={12} sm={12} lg={8}>
              <input
                className={styles.emailInput}
                placeholder={placeHolderText}
                value={email || ''}
                onKeyUp={validate}
                onChange={handleInputChange} // Call handleInputChange on input change
                type="text"
                data-testid="email-id"
                aria-label="emailInput"
              />
            </Grid>
            <Grid item xs={12} sm={12} lg={4}>
              <button
                className={`${classSubmitButton} tally_email_capture_cta`}
                type="submit"
                id="sign-up"
                data-testid="sign-up"
              >
                {buttonText}
              </button>
            </Grid>
          </Grid>
        </div>
        {((validationProps && validationProps?.isValid && successMsg) ||
          validationProps?.error) && (
          <span
            error-span={
              validationProps?.isValid ? 'email-valid' : 'email-error'
            }
            className={`${
              validationProps?.isValid ? classValidSpan : styles.mailInputError
            } ${styles.textAlignLeft}`}
          >
            {validationProps?.isValid && successMsg ? successMsg : ''}
            {validationProps?.error ? validationProps.error.label : ''}
          </span>
        )}
        <Spinner
          showSpinner={showSpinner}
          wrapperClass={styles.spinnerContainer}
        />
      </form>
    </div>
  );
}

EmailCapture.propTypes = {
  placeHolderText: PropTypes.string,
  buttonText: PropTypes.string,
  textSuccess: PropTypes.string,
  textFailed: PropTypes.string,
  successCallback: PropTypes.func,
  errorCallback: PropTypes.func,
  type: PropTypes.string,
  state: PropTypes.string,
  subContext: PropTypes.string,
};
